import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Form, Offcanvas, Spinner } from 'react-bootstrap';
import { QuestionCircle } from 'react-bootstrap-icons';
import { toast } from 'react-toastify';
import {
  useGetUserPreferencesQuery,
  useUpdateUserPreferencesMutation,
  useUploadCompanyLogoMutation,
  useGetEmailAccountsQuery,
} from '../services/api';
import EMMSectionHeader from './EMMSectionHeader';
import {
  preferencesTenantWriteSpread,
  useTenantIdForPreferences,
  useUserPreferencesQueryArg,
} from '../hooks/useTenantIdForPreferences';
import {
  getStoredTenantTimezone,
  isValidIanaTimezone,
  setStoredTenantTimezone,
} from '../utils/tenantTimezone';
import { useUser } from '../contexts/UserContext';
import { parseErmDepartmentPreferences } from './erm/departmentPreferences';
import {
  DEFAULT_SCRIPT_LIBRARY_ACCESS,
  normalizeScriptLibraryAccessConfig,
  SCRIPT_LIBRARY_ELIGIBLE_ROLES,
  type ScriptLibraryAccessConfig,
} from '../utils/scriptLibraryAccess';

type CompanyProfilePrefs = {
  companyName?: string;
  logoUrl?: string | null;
  logoUpdatedAt?: string | null;
  timezone?: string | null;
};

const DEFAULT_TIMEZONE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'UTC', label: 'UTC' },
  { value: 'America/New_York', label: 'EST/EDT (America/New_York)' },
  { value: 'America/Chicago', label: 'CST/CDT (America/Chicago)' },
  { value: 'America/Denver', label: 'MST/MDT (America/Denver)' },
  { value: 'America/Los_Angeles', label: 'PST/PDT (America/Los_Angeles)' },
  { value: 'America/Phoenix', label: 'MST (America/Phoenix)' },
  { value: 'America/Anchorage', label: 'AKST/AKDT (America/Anchorage)' },
  { value: 'Pacific/Honolulu', label: 'HST (Pacific/Honolulu)' },
];

const getTimezoneOptions = (): Array<{ value: string; label: string }> => {
  const fallback = [...DEFAULT_TIMEZONE_OPTIONS];
  if (typeof Intl === 'undefined' || typeof (Intl as any).supportedValuesOf !== 'function') {
    return fallback;
  }
  try {
    const supported = (Intl as any).supportedValuesOf('timeZone') as string[];
    const preferred = new Map<string, string>();
    fallback.forEach((option) => preferred.set(option.value, option.label));

    const byValue = new Map<string, { value: string; label: string }>();
    fallback.forEach((option) => byValue.set(option.value, option));
    supported.forEach((timezone) => {
      const value = String(timezone || '').trim();
      if (!value) return;
      if (byValue.has(value)) return;
      byValue.set(value, { value, label: value });
    });

    const preferredEntries: Array<{ value: string; label: string }> = [];
    preferred.forEach((label, value) => {
      const option = byValue.get(value);
      if (option) preferredEntries.push({ value: option.value, label });
    });
    const rest = Array.from(byValue.values())
      .filter((option) => !preferred.has(option.value))
      .sort((a, b) => a.value.localeCompare(b.value));
    return [...preferredEntries, ...rest];
  } catch {
    return fallback;
  }
};

/**
 * Company Profile (tenant-level)
 *
 * For now, this is scoped to the current logged-in tenant context (no tenant selector UI).
 * The backend stores it on the tenant-shared GLOBAL preferences row.
 */
const CompanyProfile: React.FC = () => {
  const tenantIdForPrefs = useTenantIdForPreferences();
  const userPreferencesQueryArg = useUserPreferencesQueryArg();
  const { currentUser } = useUser() as { currentUser: { role?: string } | null };
  const normalizedRole = String(currentUser?.role || '').trim().toLowerCase();
  const isAdminUser = normalizedRole === 'admin' || normalizedRole === 'administrator';

  const {
    data: prefsData,
    isFetching: isFetchingPrefs,
    error: prefsError,
  } = useGetUserPreferencesQuery(userPreferencesQueryArg, { refetchOnMountOrArgChange: true });

  const [updateUserPreferences, { isLoading: isSaving }] = useUpdateUserPreferencesMutation();
  const [uploadCompanyLogo, { isLoading: isUploading }] = useUploadCompanyLogoMutation();

  const preferences: any = prefsData?.preferences || {};
  const companyProfile: CompanyProfilePrefs = preferences.companyProfile || {};
  const logoUrl = companyProfile.logoUrl || null;
  const timezoneOptions = useMemo(() => getTimezoneOptions(), []);
  const timezoneOptionValues = useMemo(
    () => new Set(timezoneOptions.map((option) => option.value)),
    [timezoneOptions]
  );

  const [companyName, setCompanyName] = useState('');
  const [companyTimezone, setCompanyTimezone] = useState('');
  const [dmarcCollectionEmail, setDmarcCollectionEmail] = useState('');
  const [showHelp, setShowHelp] = useState(false);

  const { data: emailAccountsData } = useGetEmailAccountsQuery(undefined, { refetchOnMountOrArgChange: false });
  const emailAccountsList: string[] = useMemo(() => {
    const accounts = (emailAccountsData as any)?.data?.emailAccounts || [];
    if (!Array.isArray(accounts)) return [];
    return accounts.map((a: any) => String(a.fullEmail || a.id || a.email || '').toLowerCase().trim()).filter(Boolean);
  }, [emailAccountsData]);

  useEffect(() => {
    setCompanyName(String(companyProfile.companyName || ''));
  }, [companyProfile.companyName]);
  useEffect(() => {
    setDmarcCollectionEmail(String(preferences.dmarcCollectionEmail || '').trim());
  }, [preferences.dmarcCollectionEmail]);
  useEffect(() => {
    const timezone = String(companyProfile.timezone || '').trim();
    const fallbackTimezone = getStoredTenantTimezone(tenantIdForPrefs);
    const effectiveTimezone = isValidIanaTimezone(timezone) ? timezone : fallbackTimezone || '';
    setCompanyTimezone(effectiveTimezone);
    if (isValidIanaTimezone(effectiveTimezone)) {
      setStoredTenantTimezone(effectiveTimezone, tenantIdForPrefs);
    }
  }, [companyProfile.timezone, tenantIdForPrefs]);

  const dmarcEmailIsKnown = dmarcCollectionEmail ? emailAccountsList.includes(dmarcCollectionEmail.toLowerCase().trim()) : true;

  const handleSave = async () => {
    try {
      const timezone = String(companyTimezone || '').trim();
      const trimmedCompanyName = String(companyName || '').trim();
      const preservedCompanyName = trimmedCompanyName || String(companyProfile.companyName || '').trim();
      const preservedTimezone = isValidIanaTimezone(timezone)
        ? timezone
        : (companyProfile.timezone || null);
      const nextProfile = {
        ...(companyProfile || {}),
        companyName: preservedCompanyName,
        timezone: preservedTimezone,
      };
      await updateUserPreferences({
        ...preferencesTenantWriteSpread(tenantIdForPrefs),
        preferences: {
          companyProfile: nextProfile,
          dmarcCollectionEmail: dmarcCollectionEmail.trim() || null,
        },
      }).unwrap();
      setStoredTenantTimezone(nextProfile.timezone, tenantIdForPrefs);
      toast.success('Company Profile saved');
    } catch (e: any) {
      toast.error(e?.data?.message || e?.message || 'Failed to save Company Profile');
    }
  };

  // ── Script Library Access (Phase 2 role/department gating) ──────────────
  // Lives in the same GLOBAL prefs blob as companyProfile/dmarc. Default is
  // "enforced: false" (open to all) so existing tenants don't lose access on
  // upgrade. Admins can flip on enforcement to restrict to specific
  // roles + departments. Admins themselves always retain visibility, even
  // when not in the lists (handled in the runtime gate).
  const savedScriptLibraryAccess = useMemo<ScriptLibraryAccessConfig>(
    () => normalizeScriptLibraryAccessConfig(preferences.scriptLibraryAccess),
    [preferences.scriptLibraryAccess]
  );
  const { departments: ermDepartments } = useMemo(
    () => parseErmDepartmentPreferences(preferences),
    [preferences]
  );
  const [scriptLibraryEnforced, setScriptLibraryEnforced] = useState<boolean>(
    DEFAULT_SCRIPT_LIBRARY_ACCESS.enforced
  );
  const [scriptLibraryAllowedRoles, setScriptLibraryAllowedRoles] = useState<Set<string>>(
    new Set<string>()
  );
  const [scriptLibraryAllowedDepartments, setScriptLibraryAllowedDepartments] = useState<Set<string>>(
    new Set<string>()
  );
  const [isSavingScriptLibraryAccess, setIsSavingScriptLibraryAccess] = useState(false);

  // Hydrate local state when prefs load / change.
  useEffect(() => {
    setScriptLibraryEnforced(savedScriptLibraryAccess.enforced);
    setScriptLibraryAllowedRoles(new Set(savedScriptLibraryAccess.allowedRoles));
    setScriptLibraryAllowedDepartments(new Set(savedScriptLibraryAccess.allowedDepartments));
  }, [savedScriptLibraryAccess]);

  const handleSaveScriptLibraryAccess = async () => {
    try {
      setIsSavingScriptLibraryAccess(true);
      const next: ScriptLibraryAccessConfig = {
        enforced: scriptLibraryEnforced,
        allowedRoles: Array.from(scriptLibraryAllowedRoles)
          .map((r) => r.toLowerCase())
          .filter((r) => SCRIPT_LIBRARY_ELIGIBLE_ROLES.some((opt) => opt.id === r)),
        allowedDepartments: Array.from(scriptLibraryAllowedDepartments).filter(Boolean),
      };
      await updateUserPreferences({
        ...preferencesTenantWriteSpread(tenantIdForPrefs),
        preferences: { scriptLibraryAccess: next },
      }).unwrap();
      toast.success('Script Library access updated');
    } catch (e: any) {
      toast.error(
        e?.data?.message || e?.message || 'Failed to update Script Library access'
      );
    } finally {
      setIsSavingScriptLibraryAccess(false);
    }
  };

  const scriptLibraryAccessIsDirty = useMemo(() => {
    if (scriptLibraryEnforced !== savedScriptLibraryAccess.enforced) return true;
    const savedRoles = new Set(savedScriptLibraryAccess.allowedRoles);
    if (savedRoles.size !== scriptLibraryAllowedRoles.size) return true;
    if (Array.from(scriptLibraryAllowedRoles).some((r) => !savedRoles.has(r))) return true;
    const savedDepts = new Set(savedScriptLibraryAccess.allowedDepartments);
    if (savedDepts.size !== scriptLibraryAllowedDepartments.size) return true;
    if (Array.from(scriptLibraryAllowedDepartments).some((d) => !savedDepts.has(d))) return true;
    return false;
  }, [
    scriptLibraryEnforced,
    scriptLibraryAllowedRoles,
    scriptLibraryAllowedDepartments,
    savedScriptLibraryAccess,
  ]);

  const handleLogoSelected = async (file: File) => {
    try {
      const maxBytes = 2 * 1024 * 1024;
      if (file.size > maxBytes) {
        toast.error('Logo too large (max 2MB)');
        return;
      }
      const allowedTypes = new Set(['image/png', 'image/jpeg', 'image/gif', 'image/webp']);
      if (file.type && !allowedTypes.has(file.type)) {
        toast.error('Unsupported logo type. Use PNG/JPG/GIF/WebP.');
        return;
      }
      await uploadCompanyLogo({ ...preferencesTenantWriteSpread(tenantIdForPrefs), file }).unwrap();
      toast.success('Logo uploaded');
    } catch (e: any) {
      toast.error(e?.data?.message || e?.message || 'Failed to upload logo');
    }
  };

  return (
    <>
      <EMMSectionHeader title="Company Profile" showHelp onHelpClick={() => setShowHelp(true)} />
      <div className="pt-3" style={{ maxWidth: 900 }}>

      {prefsError && (
        <Alert variant="danger">
          Failed to load Company Profile.
        </Alert>
      )}

      <Card>
        <Card.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Company Name</Form.Label>
              <Form.Control
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="e.g. KrossKast"
                disabled={isSaving}
              />
              <Form.Text className="text-muted">
                Shared for all users in this tenant.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Company Logo</Form.Label>
              <div className="d-flex align-items-center gap-3 flex-wrap" style={{ minHeight: 64 }}>
                <div
                  style={{
                    width: 220,
                    height: 64,
                    borderRadius: 8,
                    border: '1px solid rgba(0,0,0,0.1)',
                    background: 'rgba(0,0,0,0.02)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                  }}
                  title={logoUrl || 'No logo uploaded'}
                >
                  {logoUrl ? (
                    <img
                      src={logoUrl}
                      alt={String(companyProfile.companyName || 'Company logo')}
                      style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                    />
                  ) : (
                    <span style={{ fontSize: 12, opacity: 0.6 }}>No logo</span>
                  )}
                </div>

                <div className="d-flex align-items-center gap-2">
                  <Form.Control
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                    disabled={isUploading}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const f = e.target.files && e.target.files[0];
                      if (f) handleLogoSelected(f);
                      // Allow re-selecting the same file
                      e.target.value = '';
                    }}
                    style={{ maxWidth: 320 }}
                  />
                  {isUploading && <Spinner animation="border" size="sm" />}
                </div>
              </div>
              <Form.Text className="text-muted">
                Recommended: wide rectangle logo (PNG/WebP). Max 2MB.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Tenant Timezone</Form.Label>
              <Form.Select
                value={companyTimezone}
                onChange={(e) => setCompanyTimezone(e.target.value)}
                disabled={isSaving}
              >
                <option value="">Use browser local timezone (no tenant override)</option>
                {companyTimezone && !timezoneOptionValues.has(companyTimezone) ? (
                  <option value={companyTimezone}>{companyTimezone}</option>
                ) : null}
                {timezoneOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">
                Shared tenant timezone used by major date displays (for example, Unibox timestamps).
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>DMARC Report Email</Form.Label>
              <Form.Control
                type="email"
                value={dmarcCollectionEmail}
                onChange={(e) => setDmarcCollectionEmail(e.target.value)}
                placeholder="e.g. dmarc@yourdomain.com"
                disabled={isSaving}
              />
              <Form.Text className="text-muted">
                All domains will use this as the default DMARC report destination (rua). Create a dedicated inbox and hide it from All Inboxes to keep your feed clean.
              </Form.Text>
              {dmarcCollectionEmail && !dmarcEmailIsKnown && (
                <Alert variant="warning" className="mt-2 py-2 small">
                  <strong>{dmarcCollectionEmail}</strong> is not a mailbox in KG. DMARC reports may not be received unless this inbox exists.
                </Alert>
              )}
            </Form.Group>

            <div className="d-flex gap-2 align-items-center">
              <Button variant="primary" onClick={handleSave} disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
              {isFetchingPrefs && (
                <div className="d-flex align-items-center text-muted">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Loading...
                </div>
              )}
            </div>
          </Form>
        </Card.Body>
      </Card>

      {isAdminUser && (
        <Card className="mt-3">
          <Card.Body>
            <div className="d-flex align-items-center justify-content-between mb-2">
              <div>
                <h5 className="mb-0">Script Library Access</h5>
                <div className="text-muted small">
                  Control which users can see the Script Library in the Executive Assistant and Unibox compose. Admins always retain access.
                </div>
              </div>
              <Badge bg={scriptLibraryEnforced ? 'warning' : 'success'} text={scriptLibraryEnforced ? 'dark' : undefined}>
                {scriptLibraryEnforced ? 'Restricted' : 'Open to all'}
              </Badge>
            </div>

            <Form>
              <Form.Group className="mb-3">
                <Form.Check
                  type="switch"
                  id="script-library-enforce-toggle"
                  label="Restrict Script Library access"
                  checked={scriptLibraryEnforced}
                  onChange={(e) => setScriptLibraryEnforced(e.target.checked)}
                  disabled={isSavingScriptLibraryAccess}
                />
                <Form.Text className="text-muted">
                  When off, every user in this tenant can see the Script Library (the default). When on, only the roles and departments selected below — plus admins — can see it.
                </Form.Text>
              </Form.Group>

              <div className="row g-3">
                <div className="col-md-6">
                  <Form.Label className="fw-semibold mb-1">Allowed permission roles</Form.Label>
                  <div className="border rounded p-2" style={{ background: scriptLibraryEnforced ? undefined : 'rgba(0,0,0,0.02)' }}>
                    <Form.Check
                      type="checkbox"
                      id="sla-role-admin"
                      label={<span>Admin <Badge bg="secondary" className="ms-1">Always allowed</Badge></span>}
                      checked
                      disabled
                    />
                    {SCRIPT_LIBRARY_ELIGIBLE_ROLES.map((role) => (
                      <Form.Check
                        key={role.id}
                        type="checkbox"
                        id={`sla-role-${role.id}`}
                        label={
                          <span>
                            {role.label}
                            <span className="text-muted small ms-2">{role.description}</span>
                          </span>
                        }
                        checked={scriptLibraryAllowedRoles.has(role.id)}
                        disabled={!scriptLibraryEnforced || isSavingScriptLibraryAccess}
                        onChange={(e) => {
                          setScriptLibraryAllowedRoles((prev) => {
                            const next = new Set(prev);
                            if (e.target.checked) next.add(role.id);
                            else next.delete(role.id);
                            return next;
                          });
                        }}
                      />
                    ))}
                  </div>
                </div>

                <div className="col-md-6">
                  <Form.Label className="fw-semibold mb-1">Allowed departments</Form.Label>
                  <div className="border rounded p-2" style={{ background: scriptLibraryEnforced ? undefined : 'rgba(0,0,0,0.02)' }}>
                    {ermDepartments.length === 0 ? (
                      <div className="text-muted small">
                        No departments defined. Add departments under <strong>ERM &rarr; Departments</strong> to gate by team.
                      </div>
                    ) : (
                      ermDepartments.map((dept) => {
                        const key = dept.name.trim().toLowerCase();
                        return (
                          <Form.Check
                            key={dept.id}
                            type="checkbox"
                            id={`sla-dept-${dept.id}`}
                            label={dept.name}
                            checked={scriptLibraryAllowedDepartments.has(key)}
                            disabled={!scriptLibraryEnforced || isSavingScriptLibraryAccess}
                            onChange={(e) => {
                              setScriptLibraryAllowedDepartments((prev) => {
                                const next = new Set(prev);
                                if (e.target.checked) next.add(key);
                                else next.delete(key);
                                return next;
                              });
                            }}
                          />
                        );
                      })
                    )}
                  </div>
                  <Form.Text className="text-muted">
                    Departments are matched against each user&apos;s assigned department.
                  </Form.Text>
                </div>
              </div>

              <div className="d-flex gap-2 align-items-center mt-3">
                <Button
                  variant="primary"
                  onClick={handleSaveScriptLibraryAccess}
                  disabled={isSavingScriptLibraryAccess || !scriptLibraryAccessIsDirty}
                >
                  {isSavingScriptLibraryAccess ? 'Saving...' : 'Save access settings'}
                </Button>
                {isSavingScriptLibraryAccess && <Spinner animation="border" size="sm" />}
              </div>
            </Form>
          </Card.Body>
        </Card>
      )}
      </div>

      <Offcanvas show={showHelp} onHide={() => setShowHelp(false)} placement="end" style={{ width: '400px' }}>
        <Offcanvas.Header closeButton className="border-bottom">
          <Offcanvas.Title className="d-flex align-items-center">
            <QuestionCircle className="me-2 text-primary" size={20} />
            <span className="fw-bold">Company Profile Help</span>
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <h6 className="mb-2">Company Name</h6>
          <p className="mb-3 small">
            The display name for your organization, shared across all users in this tenant. Appears in headers and reports.
          </p>

          <h6 className="mb-2">Company Logo</h6>
          <p className="mb-3 small">
            Upload a wide rectangle logo (PNG, JPG, GIF, or WebP, max 2 MB). Used in email signatures, reports, and the sidebar.
          </p>

          <h6 className="mb-2">Tenant Timezone</h6>
          <p className="mb-3 small">
            Sets the default timezone for date displays across the app (Unibox timestamps, analytics, etc.). Individual users see dates in this timezone. Select "Use browser local timezone" to let each user's browser decide.
          </p>

          <h6 className="mb-2">DMARC Report Email</h6>
          <p className="mb-3 small">
            Set <strong>one central email</strong> per tenant to receive DMARC aggregate reports (rua) for all your domains. This avoids creating a separate inbox per domain.
          </p>
          <div className="alert alert-info small py-2 mb-3">
            <strong>Recommended setup:</strong>
            <ol className="mb-0 mt-1 ps-3">
              <li>Create an email account like <code>dmarc@yourdomain.com</code> on the Email Accounts page.</li>
              <li>Enter that address here and click Save.</li>
              <li>Go to Email Accounts, open settings for the dmarc@ account, and enable <strong>"Hide from All Inboxes"</strong> so reports don't clutter your feed.</li>
              <li>Open Email Authentication (Domains page) for each domain — the rua field will auto-fill with this address.</li>
            </ol>
          </div>
          <p className="mb-3 small">
            <strong>Why?</strong> DMARC reports are machine-generated XML from providers like Gmail, Yahoo, and Microsoft. They can be high volume (hundreds/day). A dedicated hidden inbox keeps them organized without noise.
          </p>

          <h6 className="mb-2">FAQ</h6>
          <p className="mb-2 small">
            <strong>Can I use a transactional domain?</strong> Yes. The DMARC report email is just a destination mailbox — it doesn't matter whether the domain is transactional, marketing, or any other type. DMARC reports are simply delivered to whatever inbox you point the rua at.
          </p>
          <p className="mb-2 small">
            <strong>Can I use an external email (Gmail, Outlook, etc.)?</strong> Yes. DMARC senders (Gmail, Yahoo, Microsoft) will deliver reports to any valid email address. You can use an external inbox if you prefer. The only trade-off is that you won't see the reports inside KG's Unibox — you'd check them in the external provider instead.
          </p>
          <p className="mb-0 small text-muted">
            If the address is not a mailbox in KG, a soft warning will appear as a reminder. This is not a blocker — external addresses work fine.
          </p>
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
};

export default CompanyProfile;

